import { useMemo } from 'react';
import { UNKNOWN_RELEASES_WARNING } from './constants';
import { POSTS } from './posts';
import { useAnnouncementStore } from './store';
import type { Post, PostContent, Release } from './types';

/** The posts in code that user-manager has released, with their release facts, in release order. */
export const releasedPosts = (contents: PostContent[], releases: Release[]): Post[] => {
  const byId = new Map(contents.map((post) => [post.id, post]));
  return releases.flatMap(({ postId, ...facts }) => {
    const content = byId.get(postId);
    return content ? [{ ...content, ...facts }] : [];
  });
};

/** Released ids with no post in this build: a row added before its post shipped, or a typo in it. */
export const unknownReleases = (contents: PostContent[], releases: Release[]): string[] => {
  const known = new Set(contents.map((post) => post.id));
  return releases.map((release) => release.postId).filter((id) => !known.has(id));
};

/** Warns on dev and staging (prod silences console.warn), where a row is checked before prod gets it. */
export const warnUnknownReleases = (releases: Release[]): void => {
  const unknown = unknownReleases(POSTS, releases);
  if (unknown.length) console.warn(UNKNOWN_RELEASES_WARNING, unknown.join(', '));
};

/** This build's posts as user-manager released them; none until the state loads, or when it fails to. */
export function useLivePosts(): Post[] {
  const releases = useAnnouncementStore((s) => s.releases);
  return useMemo(() => releasedPosts(POSTS, releases), [releases]);
}
